import { listPublicPackagesOptions } from "@/actions/coaching-package/handler";
import { trackCoachingDurationSelect } from "@/components/analytics/conversions";
import { CoachingApplicationDialog } from "@/components/coaching/CoachingApplicationDialog";
import { CoachingPurchasePreviewDialog } from "@/components/coaching/CoachingPurchasePreviewDialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  COACHING_DURATIONS,
  type CoachingDuration,
  getCoachingDurationPrice,
} from "@/lib/coaching-duration-pricing";
import { cn } from "@/lib/utils";
import { createWhatsAppUrl } from "@/lib/whatsapp";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  Check,
  ContactRound,
  Lightbulb,
  MessageCircle,
  ShieldCheck,
  ShoppingBag,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

const coachsSearchSchema = z.object({
  payment: z.enum(["success", "failed"]).optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute("/_app/coachs")({
  validateSearch: coachsSearchSchema,
  head: () => ({
    title:
      "Koçluk Paketleri - Online Fitness ve Antrenman Programları | Alican Atalay",
    meta: [
      {
        name: "description",
        content:
          "Alican Atalay ile online koçluk, fitness ve antrenman programları. Başlangıç ve ileri seviye sporcular için kişiselleştirilmiş kuvvet, mobilite ve beslenme rehberi. WhatsApp destekli, video anlatımlı, hedef odaklı koçluk paketleri.",
      },
      {
        name: "keywords",
        content:
          "Alican Atalay, online koçluk, fitness programı, antrenman paketi, kuvvet programı, beslenme rehberi, sporcu koçu, WhatsApp destekli koçluk, video egzersiz rehberi, kişisel antrenör, platoları aşma, başlangıç fitness, online spor danışmanlığı, Atalay The Coach",
      },
    ],
  }),

  loader: async ({ context }) =>
    await context.queryClient.ensureQueryData(listPublicPackagesOptions()),
  component: PricingPageComponent,
});

interface PlanItem {
  packageId?: string;
  title: string;
  description: string;
  price: number;
  oldPrice?: number;
  priceDetails: string;
  features: string[];
  continuationPlan?: string;
  ctaText?: string;
  isRecommended?: boolean;
  imageUrl?: string;
}

// PricingGrid component to render the cards
function PricingGrid({
  plans,
  duration,
}: {
  plans: PlanItem[];
  duration?: CoachingDuration;
}) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-2 xl:gap-10">
      {plans.map((plan) => {
        const durationPrice = duration
          ? getCoachingDurationPrice(plan.title, duration)
          : null;
        const selectedPackageTitle = durationPrice
          ? `${plan.title} · ${duration} Aylık`
          : plan.title;
        const finalPrice = durationPrice?.total ?? plan.price;
        const finalOldPrice = durationPrice?.savings
          ? durationPrice.regularTotal
          : plan.oldPrice;

        return (
          <Card
            key={plan.packageId || plan.title}
            className={cn(
              "group relative flex flex-col overflow-hidden border-2 shadow-lg transition-all duration-300 hover:scale-[1.01] hover:shadow-xl",
              plan.isRecommended
                ? "border-primary/20 bg-secondary/5"
                : "border-border hover:border-primary/30",
            )}
          >
            {/* Package Image */}
            {plan.imageUrl && (
              <div className="relative flex h-48 w-full items-center justify-center overflow-hidden bg-muted/30 p-2">
                <img
                  src={plan.imageUrl}
                  alt={plan.title}
                  className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Premium badge overlay for recommended packages */}
                {plan.isRecommended && (
                  <div className="absolute top-4 right-4 z-10">
                    <span className="rounded-full bg-primary px-3 py-1 font-semibold text-primary-foreground text-xs shadow-lg">
                      ÖNERİLEN
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Premium badge for recommended packages (when no image) */}
            {!plan.imageUrl && plan.isRecommended && (
              <div className="absolute top-4 right-4 z-10">
                <span className="rounded-full bg-primary px-3 py-1 font-semibold text-primary-foreground text-xs">
                  ÖNERİLEN
                </span>
              </div>
            )}

            <CardHeader className="pt-6 pb-6">
              <div className="space-y-2">
                <CardTitle className="font-bold text-xl leading-tight sm:text-2xl">
                  {plan.title}
                </CardTitle>
                <CardDescription className="text-sm leading-relaxed sm:text-base">
                  {plan.description}
                </CardDescription>
              </div>

              {/* Price section */}
              <div className="flex flex-col justify-start pt-4">
                <div className="flex items-baseline space-x-2">
                  {durationPrice?.savings ? (
                    <span className="font-medium text-lg text-muted-foreground line-through">
                      ₺{durationPrice.regularTotal.toLocaleString("tr-TR")}
                    </span>
                  ) : plan.oldPrice && plan.oldPrice !== plan.price ? (
                    <span className="font-medium text-lg text-muted-foreground line-through">
                      ₺{plan.oldPrice.toLocaleString("tr-TR")}
                    </span>
                  ) : null}
                  <span className="font-extrabold text-3xl text-primary tracking-tight sm:text-4xl">
                    ₺{finalPrice.toLocaleString("tr-TR")}
                  </span>
                </div>
                <p className="mt-2 text-muted-foreground text-xs sm:text-left sm:text-sm">
                  {durationPrice
                    ? `KDV dahil · ${duration} aylık toplam · ${durationPrice.monthlyEquivalent.toLocaleString("tr-TR", { maximumFractionDigits: 0 })} ₺/ay`
                    : plan.priceDetails}
                </p>
                <div className="flex justify-start">
                  {durationPrice?.savings ? (
                    <div className="mt-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-1">
                      <span className="font-medium text-primary text-xs">
                        %{durationPrice.discountPercent} avantaj · ₺
                        {durationPrice.savings.toLocaleString("tr-TR")} tasarruf
                      </span>
                    </div>
                  ) : plan.oldPrice && plan.oldPrice !== plan.price ? (
                    <div className="mt-2 inline-flex items-center rounded-full bg-destructive/10 px-2 py-1">
                      <span className="font-medium text-destructive text-xs">
                        %
                        {Math.round(
                          ((plan.oldPrice - plan.price) / plan.oldPrice) * 100,
                        )}{" "}
                        İNDİRİM
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            </CardHeader>

            <CardContent className="grow px-6 pb-6">
              <div className="space-y-3">
                <h4 className="font-semibold text-muted-foreground text-sm uppercase tracking-wide">
                  {plan.isRecommended ? "Paket İçeriği" : "Hizmet İçeriği"}
                </h4>
                <ul className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <div className="mt-0.5 shrink-0">
                        <Check className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-foreground text-sm leading-relaxed">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>

            <CardFooter className="border-t bg-accent/20 p-6">
              <div className="w-full space-y-3">
                {plan.continuationPlan && (
                  <div className="rounded-lg border border-muted-foreground/30 border-dashed bg-muted/30 p-3">
                    <p className="text-center text-muted-foreground text-xs sm:text-sm">
                      <Lightbulb className="mr-1 inline h-4 w-4" />
                      {plan.continuationPlan}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <CoachingApplicationDialog
                    packageTitle={selectedPackageTitle}
                  >
                    <Button
                      type="button"
                      data-analytics-cta={`coaching_application_${selectedPackageTitle}`}
                      variant="outline"
                      size="default"
                      className="w-full"
                    >
                      Koçluk Başvurusu
                    </Button>
                  </CoachingApplicationDialog>

                  <Button
                    asChild
                    type="button"
                    variant="outline"
                    size="default"
                  >
                    <a
                      href={createWhatsAppUrl(
                        `Merhaba, ${selectedPackageTitle} paketi hakkında bilgi almak istiyorum.`,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1.5"
                    >
                      <MessageCircle className="h-4 w-4 text-emerald-500" />
                      WhatsApp'tan Sor
                    </a>
                  </Button>
                </div>

                {plan.packageId ? (
                  <CoachingPurchasePreviewDialog
                    packageId={plan.packageId}
                    packageTitle={selectedPackageTitle}
                    durationMonths={duration}
                    price={finalPrice}
                    oldPrice={finalOldPrice}
                    features={plan.features}
                  >
                    <Button
                      size="lg"
                      data-analytics-cta={`package_purchase_${selectedPackageTitle}`}
                      className="w-full bg-primary font-semibold text-primary-foreground shadow-md transition-all duration-200 hover:bg-primary/90 hover:shadow-lg active:scale-[0.98]"
                    >
                      <ShoppingBag className="mr-2 h-4 w-4" />
                      {plan.ctaText || "Paketi Satın Al"}
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </CoachingPurchasePreviewDialog>
                ) : (
                  <Button
                    asChild
                    size="lg"
                    className="w-full bg-primary font-semibold text-primary-foreground shadow-md transition-all duration-200 hover:bg-primary/90 hover:shadow-lg active:scale-[0.98]"
                  >
                    <a
                      href={createWhatsAppUrl(
                        `Merhaba, ${selectedPackageTitle} hizmetini satın almak istiyorum.`,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="h-4 w-4" />
                      WhatsApp ile Satın Al
                    </a>
                  </Button>
                )}
              </div>
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
}

function PricingPageComponent() {
  const { data } = useSuspenseQuery(listPublicPackagesOptions());
  const search = Route.useSearch();
  const navigate = useNavigate();

  const [duration, setDuration] = useState<CoachingDuration>(3);

  // Handle payment failure feedback if redirected back to coachs
  useEffect(() => {
    if (search.payment === "failed") {
      toast.error(
        search.error ||
          "Ödeme işlemi gerçekleştirilemedi. Lütfen tekrar deneyin.",
        { duration: 6000 },
      );
      void navigate({ to: "/coachs", search: {}, replace: true });
    }
  }, [search, navigate]);

  const coachingPackages = data.body?.coaching || [];
  const consultingServices = data.body?.consulting || [];

  return (
    <div className="min-h-screen w-full bg-background px-4 pt-28 pb-20 text-foreground antialiased sm:px-8">
      <div className="mx-auto w-full max-w-7xl">
        {/* Hero Section */}
        <div className="mb-16 text-center sm:mb-20">
          <div className="mx-auto max-w-3xl space-y-6">
            <h1 className="font-extrabold text-4xl text-foreground tracking-tight sm:text-5xl lg:text-6xl">
              Koçluk ve Hizmetler
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground leading-relaxed sm:text-xl">
              Fitness hedeflerinize ulaşmak için profesyonel destek alın.
              Kişiselleştirilmiş programlar ve uzman rehberliği ile başarıya
              adım atın.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 pt-4 text-muted-foreground text-sm">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                <span>Kişiye Özel Programlar</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                <span>Uzman Rehberlik</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" />
                <span>WhatsApp Desteği</span>
              </div>
            </div>
            <div className="pt-2">
              <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
                <CoachingApplicationDialog packageTitle="Genel Koçluk Başvurusu">
                  <Button
                    size="lg"
                    data-analytics-cta="coaching_application_general"
                    className="min-w-56 shadow-lg shadow-primary/20"
                  >
                    Koçluk Başvurusu Yap
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CoachingApplicationDialog>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="min-w-56"
                >
                  <a
                    href={createWhatsAppUrl(
                      "Merhaba, koçluk hizmetleri hakkında bilgi almak istiyorum.",
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    WhatsApp'tan Bilgi Al
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="coaching" className="w-full">
          <TabsList className="mx-auto mb-12 grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="coaching" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Koçluk Paketleri</span>
              <span className="sm:hidden">Koçluk</span>
            </TabsTrigger>
            <TabsTrigger value="consulting" className="flex items-center gap-2">
              <ContactRound className="h-4 w-4" />
              <span className="hidden sm:inline">Danışmanlık</span>
              <span className="sm:hidden">Danışmanlık</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="coaching" className="mt-0">
            <div className="mb-8 text-center">
              <h2 className="font-bold text-2xl text-foreground sm:text-3xl">
                Koçluk Paketleri
              </h2>
              <p className="mt-2 text-muted-foreground">
                Uzun vadeli hedefleriniz için kapsamlı destek programları
              </p>
              <div className="mx-auto mt-6 grid max-w-2xl grid-cols-2 gap-2 rounded-xl bg-muted p-1.5 sm:grid-cols-4">
                {COACHING_DURATIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      setDuration(option);
                      trackCoachingDurationSelect(option);
                    }}
                    className={cn(
                      "relative rounded-lg px-3 py-2.5 font-semibold text-sm transition-all",
                      duration === option
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {option === 1 ? "Aylık" : `${option} Aylık`}
                    {option === 3 && (
                      <span className="ml-1 text-[9px] text-primary uppercase">
                        Önerilen
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <PricingGrid plans={coachingPackages} duration={duration} />
          </TabsContent>

          <TabsContent value="consulting" className="mt-0">
            <div className="mb-8 text-center">
              <h2 className="font-bold text-2xl text-foreground sm:text-3xl">
                Danışmanlık Hizmetleri
              </h2>
              <p className="mt-2 text-muted-foreground">
                Tek seferlik uzman görüşleri ve teknik analizler
              </p>
            </div>
            <PricingGrid plans={consultingServices} />
          </TabsContent>
        </Tabs>

        {/* Sade Güvenli Ödeme & Sözleşmeler Bandı */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-xl border border-border/60 bg-muted/20 px-5 py-4 text-center sm:flex-row sm:text-left">
          <div className="space-y-1">
            <div className="flex items-center justify-center gap-1.5 font-medium text-foreground text-xs sm:justify-start">
              <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
              <span>
                İyzico altyapısıyla 256-bit SSL şifrelemeli 3D Secure güvenli
                ödeme
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-muted-foreground text-xs sm:justify-start">
              <Link
                to="/privacy"
                search={{ tab: "distance-sales" }}
                className="text-primary hover:underline"
              >
                Mesafeli Satış Sözleşmesi
              </Link>
              <span>•</span>
              <Link
                to="/privacy"
                search={{ tab: "delivery-return" }}
                className="text-primary hover:underline"
              >
                Teslimat & İade Politikası
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
